// Mock data for the Finance page (`/finance`). Single source until the
// reconciliation layer between DAITEC Genba Plus finance (primary) and
// Trusstor labour/equipment overlays lands on the backend. Per the
// roadmap data-mapping, primary finance ownership is DAITEC; Trusstor
// provides the live spend-today / labour / equipment overlays.
//
// Values mirror the Loveable prototype (command-center-glimpse.lovable.app/finance)
// 1:1 so a side-by-side comparison reads the same. Do not "round" or
// "clean up" numbers — the screenshot is the spec.
import type {
  IFinanceData,
  IBudgetVsActualRow,
  ICostHeatmapFloor,
  IInvoiceRow,
  ILaborCostRow,
  IEquipmentCostRow,
  IBudgetCategoryRow,
  IQuoteGroup,
  IOrderRow,
  IPaymentRow,
  IAiFinancialInsight,
} from '@interfaces/IFinance';

/* ---------------------------------------------------------------
   Summary rail (shared across all tabs — left card)
--------------------------------------------------------------- */

const SUMMARY = {
  totalSpendToday: 48_320,
  budgetVsActualPct: 94.2,
  outstandingInvoices: 12,
  pendingPayments: 7,
  activeOrders: 23,
};

/* ---------------------------------------------------------------
   Filters — choices populating the four shared selects
--------------------------------------------------------------- */

const FILTERS = {
  floors: ['All Floors', ...Array.from({ length: 20 }, (_, i) => `F${20 - i}`)],
  trades: [
    { id: 'all', label: 'All Trades' },
    { id: 'electrical', label: 'Electrical' },
    { id: 'plumbing', label: 'Plumbing' },
    { id: 'concrete', label: 'Concrete' },
    { id: 'hvac', label: 'HVAC' },
    { id: 'steel', label: 'Steel' },
    { id: 'general-labor', label: 'General Labor' },
  ],
  contractors: [
    { id: 'all', label: 'All Contractors' },
    { id: 'apex-electric', label: 'Apex Electric' },
    { id: 'bluepipe', label: 'BluePipe Ltd' },
    { id: 'concretepro', label: 'ConcretePro' },
    { id: 'steelworks', label: 'SteelWorks Inc' },
    { id: 'hvac-solutions', label: 'HVAC Solutions' },
    { id: 'glasstech', label: 'GlassTech Pro' },
    { id: 'coolair', label: 'CoolAir Systems' },
  ],
  timeRanges: [
    { id: 'this-month', label: 'This Month' },
    { id: 'last-month', label: 'Last Month' },
    { id: 'this-quarter', label: 'This Quarter' },
    { id: 'ytd', label: 'Year to Date' },
  ],
};

/* ---------------------------------------------------------------
   Overview tab — 4 KPI cards + Budget vs Actual by Trade
   + Cost Heatmap by Floor + AI Financial Insights
--------------------------------------------------------------- */

const OVERVIEW_KPIS = {
  totalProjectBudget: 12_400_000,
  totalProjectBudgetLabel: 'Harborview Tower',
  spentToDate: 8_760_000,
  spentToDateUtilisedPct: 70.6,
  forecastedOverrun: 340_000,
  forecastedOverrunVariancePct: 2.7,
  costAlerts: 6,
  costAlertsCritical: 3,
};

const BUDGET_VS_ACTUAL: IBudgetVsActualRow[] = [
  { trade: 'electrical', spent: 128_400, budget: 120_000, tone: 'red'    }, // 107.0%
  { trade: 'plumbing',   spent: 87_300,  budget: 95_000,  tone: 'pink'   }, // 91.9%
  { trade: 'concrete',   spent: 224_700, budget: 210_000, tone: 'red'    }, // 107.0%
  { trade: 'hvac',       spent: 74_100,  budget: 78_000,  tone: 'green'  }, // 95.0%
  { trade: 'steel',      spent: 159_500, budget: 165_000, tone: 'purple' }, // 96.7%
];

const COST_HEATMAP: ICostHeatmapFloor[] = [
  { floor: 'F20', amount: 32_000, utilisationPct: 91,                       tone: 'green' },
  { floor: 'F19', amount: 28_500, utilisationPct: 95,                       tone: 'green' },
  { floor: 'F18', amount: 41_200, utilisationPct: 0,  overrunDeltaPct: 8,   tone: 'red'   },
  { floor: 'F17', amount: 22_800, utilisationPct: 91,                       tone: 'green' },
  { floor: 'F16', amount: 36_100, utilisationPct: 0,  overrunDeltaPct: 6,   tone: 'red'   },
  { floor: 'F15', amount: 19_400, utilisationPct: 88,                       tone: 'green' },
  { floor: 'F14', amount: 27_600, utilisationPct: 99,                       tone: 'green' },
  { floor: 'F13', amount: 33_900, utilisationPct: 0,  overrunDeltaPct: 6,   tone: 'red'   },
  { floor: 'F12', amount: 45_100, utilisationPct: 0,  overrunDeltaPct: 19,  tone: 'red'   },
  { floor: 'F11', amount: 24_300, utilisationPct: 93,                       tone: 'green' },
];

const AI_INSIGHTS: IAiFinancialInsight[] = [
  {
    id: 'ai-1',
    body: 'Floor 12 electrical 18% over planned cost — increased labor hours',
    tone: 'over-budget',
    actionLabel: 'Over Budget',
  },
  {
    id: 'ai-2',
    body: 'Concrete delivery #PO-4421 cost variance +$4,200 vs quote',
    tone: 'over-budget',
    actionLabel: 'Over Budget',
  },
  {
    id: 'ai-3',
    body: 'HVAC trade running 5% under budget — ahead of schedule',
    tone: 'under-budget',
    actionLabel: 'Under Budget',
  },
];

/* ---------------------------------------------------------------
   Costs tab — Labor + Equipment breakdown
--------------------------------------------------------------- */

const COSTS_KPIS = {
  laborCostToday: 53_271,
  equipmentCostToday: 5_710,
  totalCostToday: 58_981,
};

const LABOR_ROWS: ILaborCostRow[] = [
  { trade: 'electrical',    workers: 18, ratePerHour: 85, hoursLabel: '6.5h', cost: 9_945  },
  { trade: 'plumbing',      workers: 12, ratePerHour: 78, hoursLabel: '7h',   cost: 6_552  },
  { trade: 'concrete',      workers: 24, ratePerHour: 72, hoursLabel: '8h',   cost: 13_824 },
  { trade: 'hvac',          workers: 8,  ratePerHour: 90, hoursLabel: '5.5h', cost: 3_960  },
  { trade: 'steel',         workers: 14, ratePerHour: 95, hoursLabel: '7h',   cost: 9_310  },
  { trade: 'general-labor', workers: 22, ratePerHour: 55, hoursLabel: '8h',   cost: 9_680  },
];

const EQUIPMENT_ROWS: IEquipmentCostRow[] = [
  { id: 'eq-1', name: 'Tower Crane #1',  status: 'active',  ratePerHour: 320, hoursLabel: '7.5h', cost: 2_400 },
  { id: 'eq-2', name: 'Material Hoist A', status: 'active', ratePerHour: 180, hoursLabel: '8h',   cost: 1_440 },
  { id: 'eq-3', name: 'Concrete Pump',   status: 'idle',    ratePerHour: 250, hoursLabel: '3h',   cost: 750   },
  { id: 'eq-4', name: 'Generator G-4',   status: 'active',  ratePerHour: 95,  hoursLabel: '8h',   cost: 760   },
  { id: 'eq-5', name: 'Excavator EX-2',  status: 'idle',    ratePerHour: 210, hoursLabel: '0h',   cost: 0     },
  { id: 'eq-6', name: 'Scaffold System B', status: 'active', ratePerHour: 45, hoursLabel: '8h',   cost: 360   },
];

/* ---------------------------------------------------------------
   Budget tab — Total / Spent / Variance / Utilisation + table
--------------------------------------------------------------- */

const BUDGET_KPIS = {
  totalBudgetUsd: 12_990_000,
  spentToDateUsd: 10_910_000,
  varianceUsdK: -2075,
  budgetUtilisationPct: 84.0,
};

const BUDGET_CATEGORIES: IBudgetCategoryRow[] = [
  { category: 'Structural Works',    phase: 'Phase 1', budgetK: 2400, spentK: 2280, varianceK: -120,  progressPct: 95, status: 'on-track'     },
  { category: 'MEP Rough-In',        phase: 'Phase 2', budgetK: 1850, spentK: 1720, varianceK: -130,  progressPct: 93, status: 'on-track'     },
  { category: 'Concrete & Formwork', phase: 'Phase 1', budgetK: 3100, spentK: 3340, varianceK:  240,  progressPct: 100, status: 'over-budget' },
  { category: 'Steel Erection',      phase: 'Phase 1', budgetK: 1600, spentK: 1540, varianceK:  -60,  progressPct: 96, status: 'on-track'     },
  { category: 'Electrical Fitout',   phase: 'Phase 2', budgetK:  980, spentK: 1060, varianceK:   80,  progressPct: 100, status: 'over-budget' },
  { category: 'Plumbing & Fire',     phase: 'Phase 2', budgetK:  720, spentK:  610, varianceK: -110,  progressPct: 85, status: 'under-budget' },
  { category: 'Façade & Cladding',   phase: 'Phase 3', budgetK: 1450, spentK:  320, varianceK: -1130, progressPct: 22, status: 'on-track'     },
  { category: 'Interior Finishes',   phase: 'Phase 3', budgetK:  890, spentK:   45, varianceK:  -845, progressPct: 5,  status: 'on-track'     },
];

/* ---------------------------------------------------------------
   Invoices tab — 7 contractor invoices, mixed states
--------------------------------------------------------------- */

const INVOICES: IInvoiceRow[] = [
  { id: 'INV-2024-0087', contractor: 'Apex Electric',  amountUsd: 42_500, activity: 'F12 Electrical Rough-In', floor: '12',    date: '2026-03-15', status: 'approved'    },
  { id: 'INV-2024-0088', contractor: 'BluePipe Ltd',   amountUsd: 28_300, activity: 'F10 Plumbing Stack',      floor: '10',    date: '2026-03-14', status: 'submitted'   },
  { id: 'INV-2024-0089', contractor: 'ConcretePro',    amountUsd: 67_800, activity: 'F18-20 Slab Pour',        floor: '18-20', date: '2026-03-12', status: 'paid'        },
  { id: 'INV-2024-0090', contractor: 'SteelWorks Inc', amountUsd: 95_200, activity: 'Core Steel Erection',     floor: '16',    date: '2026-03-16', status: 'draft'       },
  { id: 'INV-2024-0091', contractor: 'Apex Electric',  amountUsd: 18_700, activity: 'F8 Panel Install',        floor: '8',     date: '2026-03-13', status: 'submitted'   },
  { id: 'INV-2024-0092', contractor: 'BluePipe Ltd',   amountUsd: 31_400, activity: 'F14 Fire Sprinkler',      floor: '14',    date: '2026-03-11', status: 'approved'    },
  { id: 'INV-2024-0093', contractor: 'HVAC Solutions', amountUsd: 54_600, activity: 'F6-10 Ductwork',          floor: '6-10',  date: '2026-03-10', status: 'discrepancy' },
];

/* ---------------------------------------------------------------
   Quotes tab — 3 RFQ groups with supplier options
--------------------------------------------------------------- */

const QUOTES: IQuoteGroup[] = [
  {
    id: 'rfq-electrical-p3',
    title: 'Phase 3 Electrical Fitout',
    budgetUsd: 420_000,
    options: [
      { id: 'q-1', supplier: 'Apex Electric', priceUsd: 395_000, leadLabel: '6 weeks',  scopeNote: 'Full scope inc. panels',   tone: 'green',   selected: true },
      { id: 'q-2', supplier: 'VoltWorks Co',  priceUsd: 412_000, leadLabel: '8 weeks',  scopeNote: 'Full scope exc. panels',   tone: 'amber'   },
      { id: 'q-3', supplier: 'PowerGrid Ltd', priceUsd: 378_000, leadLabel: '10 weeks', scopeNote: 'Partial — main feeds only', tone: 'green'   },
    ],
  },
  {
    id: 'rfq-facade',
    title: 'Façade Cladding System',
    budgetUsd: 890_000,
    options: [
      { id: 'q-4', supplier: 'GlassTech Pro',  priceUsd: 845_000, leadLabel: '12 weeks', scopeNote: 'Unitized curtain wall',          tone: 'green' },
      { id: 'q-5', supplier: 'FaçadeMasters',  priceUsd: 912_000, leadLabel: '10 weeks', scopeNote: 'Full system + warranty',         tone: 'amber' },
      { id: 'q-6', supplier: 'BuildSkin Inc',  priceUsd: 798_000, leadLabel: '14 weeks', scopeNote: 'Material only — install sep.',   tone: 'green' },
    ],
  },
  {
    id: 'rfq-hvac-rtu',
    title: 'HVAC Rooftop Units',
    budgetUsd: 165_000,
    options: [
      { id: 'q-7', supplier: 'CoolAir Systems',    priceUsd: 158_000, leadLabel: '4 weeks', scopeNote: '3x RTU + install',           tone: 'green',  selected: true },
      { id: 'q-8', supplier: 'ClimateControl Co', priceUsd: 172_000, leadLabel: '3 weeks', scopeNote: '3x RTU + install + 2y warranty', tone: 'amber' },
    ],
  },
];

/* ---------------------------------------------------------------
   Orders tab — open purchase orders
--------------------------------------------------------------- */

const ORDERS: IOrderRow[] = [
  { id: 'PO-4418', supplier: 'SteelWorks Inc', material: 'Rebar Grade 60',    quantityLabel: '24 tons',  amountUsd: 38_400,  eta: '2026-03-10', status: 'delivered'  },
  { id: 'PO-4419', supplier: 'ConcretePro',    material: 'Ready Mix C40',     quantityLabel: '180 m³',   amountUsd: 27_000,  eta: '2026-03-18', status: 'in-transit' },
  { id: 'PO-4420', supplier: 'BuildSupply Co', material: 'Drywall Sheets',    quantityLabel: '1,200 pcs', amountUsd: 14_400,  eta: '2026-03-22', status: 'ordered'    },
  { id: 'PO-4421', supplier: 'GlassTech Pro',  material: 'Curtain Wall Units', quantityLabel: '48 units', amountUsd: 192_000, eta: '2026-03-20', status: 'in-transit' },
  { id: 'PO-4422', supplier: 'CoolAir Systems', material: 'RTU Units',         quantityLabel: '3 units',  amountUsd: 158_000, eta: '2026-04-01', status: 'ordered'    },
  { id: 'PO-4423', supplier: 'Apex Electric',  material: 'Distribution Panels', quantityLabel: '12 pcs', amountUsd: 36_000,  eta: '2026-03-08', status: 'delivered'  },
];

/* ---------------------------------------------------------------
   Payments tab — schedule + KPI strip
--------------------------------------------------------------- */

const PAYMENTS_KPIS = {
  pendingApprovalUsdK: 124,
  approvedReadyUsdK: 74,
  paidThisMonthUsdK: 68,
};

const PAYMENTS_SCHEDULE: IPaymentRow[] = [
  { id: 'PAY-0041', contractor: 'ConcretePro',    invoiceId: 'INV-2024-0089', amountUsd: 67_800, milestone: 'Slab Pour F18-20',       dueDate: '2026-03-20', status: 'paid'     },
  { id: 'PAY-0042', contractor: 'Apex Electric',  invoiceId: 'INV-2024-0087', amountUsd: 42_500, milestone: 'Electrical Rough-In F12', dueDate: '2026-03-25', status: 'approved' },
  { id: 'PAY-0043', contractor: 'BluePipe Ltd',   invoiceId: 'INV-2024-0088', amountUsd: 28_300, milestone: 'Plumbing Stack F10',     dueDate: '2026-03-28', status: 'pending'  },
  { id: 'PAY-0044', contractor: 'SteelWorks Inc', invoiceId: 'INV-2024-0090', amountUsd: 95_200, milestone: 'Core Steel Erection F16', dueDate: '2026-04-01', status: 'pending'  },
  { id: 'PAY-0045', contractor: 'BluePipe Ltd',   invoiceId: 'INV-2024-0092', amountUsd: 31_400, milestone: 'Fire Sprinkler F14',     dueDate: '2026-03-30', status: 'approved' },
];

/* ---------------------------------------------------------------
   Root export
--------------------------------------------------------------- */

export const MOCK_FINANCE: IFinanceData = {
  summary: SUMMARY,
  filters: FILTERS,
  overview: {
    kpis: OVERVIEW_KPIS,
    budgetVsActual: BUDGET_VS_ACTUAL,
    costHeatmap: COST_HEATMAP,
    aiInsights: AI_INSIGHTS,
  },
  costs: {
    kpis: COSTS_KPIS,
    labor: LABOR_ROWS,
    equipment: EQUIPMENT_ROWS,
  },
  budget: {
    kpis: BUDGET_KPIS,
    categories: BUDGET_CATEGORIES,
  },
  invoices: INVOICES,
  quotes: QUOTES,
  orders: ORDERS,
  payments: {
    kpis: PAYMENTS_KPIS,
    schedule: PAYMENTS_SCHEDULE,
  },
};
